import React from "react";
import {
  DateField,
  FunctionField,
  List,
  TextField,
  useDataProvider,
  useListContext,
  useNotify,
  useSidebarState,
  useTranslate,
} from "react-admin";
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FileDownloadOutlined as ExportIcon } from "@mui/icons-material";
import {
  CustomDatagrid,
  FilterField,
  ListActionsBar,
} from "@/components/common";
import { TopbarActions } from "@/layouts";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import type { NotificationLogRecord } from "../types/log";
import ChannelDeliveryBadge from "./ChannelDeliveryBadge";
import { ChannelFilterSelect, DateFilterField } from "./LogFilterControls";
import LogRowMenu from "./LogRowMenu";

const i18nBase = "resources.notification-logs";

/** CSV cell: quote when needed, keep Excel (es-ES) happy with ";". */
const csvCell = (value: string): string =>
  /[";\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

const ListActions: React.FC = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { filterValues } = useListContext();
  const [exporting, setExporting] = React.useState(false);
  const f = (key: string) => translate(`${i18nBase}.filters.${key}`);
  const h = (key: string) => translate(`${i18nBase}.fields.${key}`);

  const handleExport = async (): Promise<void> => {
    if (exporting) return;
    setExporting(true);
    try {
      const { data } = await dataProvider.getList<NotificationLogRecord>(
        "notification-logs",
        {
          pagination: { page: 1, perPage: 10000 },
          sort: { field: "0", order: "ASC" },
          filter: filterValues ?? {},
        },
      );
      const headers = [
        h("sentAt"),
        h("origin"),
        h("originAccount"),
        h("originReference"),
        h("destination"),
        h("messageType"),
        h("status"),
        h("messageId"),
        h("from"),
        h("provider"),
        h("webhookUrl"),
        h("webhookStatus"),
        translate(`${i18nBase}.fields.error`, { _: "Error" }),
      ];
      const lines = data.map((r) =>
        [
          r.sentAt ?? "",
          r.origin ?? "",
          r.originAccount ?? "",
          r.originReference ?? "",
          r.to ?? "",
          r.messageType ?? "",
          r.status ?? "",
          r.messageId ?? "",
          r.from ?? "",
          r.provider ?? "",
          r.webhookUrl ?? "",
          r.webhookStatus ?? "",
          r.error ?? "",
        ]
          .map(csvCell)
          .join(";"),
      );
      // BOM so Excel opens UTF-8 accents correctly.
      const csv =
        "﻿" + [headers.map(csvCell).join(";"), ...lines].join("\r\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `notify-notification-logs-${stamp}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error: unknown) {
      notify(
        extractErrorMessage(error, "ra.notification.http_error", translate),
        { type: "error" },
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <TopbarActions>
        <Button
          variant="outlined"
          onClick={handleExport}
          disabled={exporting}
          startIcon={<ExportIcon sx={{ fontSize: 15 }} />}
        >
          {translate(`${i18nBase}.actions.exportCsv`, { _: "Export CSV" })}
        </Button>
      </TopbarActions>
      <ListActionsBar
        filters={
          <>
            <FilterField source="destination" label={f("destination")} />
            <FilterField source="origin" label={f("origin")} />
            <FilterField source="originAccount" label={f("originAccount")} />
            <ChannelFilterSelect
              label={f("channel")}
              allLabel={f("allChannels")}
            />
          </>
        }
        advancedFilters={
          <>
            <FilterField source="sender" label={f("sender")} sx={{ width: "100%" }} />
            <FilterField
              source="originReference"
              label={f("originReference")}
              sx={{ width: "100%" }}
            />
            <FilterField source="messageId" label={f("messageId")} sx={{ width: "100%" }} />
            <FilterField
              source="messageType"
              label={f("messageType")}
              sx={{ width: "100%" }}
            />
            <FilterField source="provider" label={f("provider")} sx={{ width: "100%" }} />
            <FilterField source="status" label={f("status")} sx={{ width: "100%" }} />
            <FilterField
              source="webhookStatus"
              label={f("webhookStatus")}
              sx={{ width: "100%" }}
            />
            <DateFilterField source="sentFrom" label={f("sentFrom")} sx={{ width: "100%" }} />
            <DateFilterField source="sentTo" label={f("sentTo")} sx={{ width: "100%" }} />
          </>
        }
      />
    </>
  );
};

export const NotificationLogsList: React.FC = () => {
  const translate = useTranslate();
  const [sidebarOpen] = useSidebarState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const maxWidth = isMobile ? "100%" : sidebarOpen ? "85vw" : "95vw";
  return (
    <Box sx={{ maxWidth }}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: "text.primary",
          mb: 1,
          mt: 0,
          ml: 2,
        }}
      >
        {translate(`${i18nBase}.name`, { smart_count: 2 })}
      </Typography>
      <List
        actions={<ListActions />}
        sort={{ field: "0", order: "ASC" }}
        storeKey={false}
        disableSyncWithLocation
        perPage={10}
        exporter={false}
        sx={{
          maxWidth: "100%",
          overflow: { xs: "visible", sm: "hidden" },
          textOverflow: { sm: "ellipsis" },
          whiteSpace: { sm: "nowrap" },
          "& .RaList-main, & .RaList-content": {
            overflow: "visible",
            width: "100%",
            maxWidth: "100%",
          },
          // The filter bar needs the full toolbar width — RA's default
          // toolbar right-aligns a compact actions slot, which left the
          // Filters button floating misaligned.
          "& .RaListToolbar-root, & .RaList-actions": {
            display: "flex",
            width: "100%",
            mb: { xs: 1, sm: 2 },
            px: 0,
            py: { xs: 0, sm: 1 },
            minHeight: "auto",
          },
        }}
      >
        <CustomDatagrid
          primarySource="to"
          bulkActionButtons={false}
          disableMobileView
        >
          <FunctionField label="" render={() => <LogRowMenu />} />
          <DateField
            source="sentAt"
            showTime
            options={{
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }}
          />
          <FunctionField
            source="origin"
            label={translate(`${i18nBase}.fields.origin`)}
            render={(record: NotificationLogRecord) => record.origin || "—"}
          />
          <FunctionField
            source="originAccount"
            label={translate(`${i18nBase}.fields.originAccount`)}
            render={(record: NotificationLogRecord) =>
              record.originAccount || "—"
            }
          />
          <FunctionField
            source="originReference"
            label={translate(`${i18nBase}.fields.originReference`)}
            render={(record: NotificationLogRecord) =>
              record.originReference || "—"
            }
          />
          <TextField
            source="to"
            label={translate(`${i18nBase}.fields.destination`)}
          />
          <FunctionField
            source="provider"
            label={translate(`${i18nBase}.fields.provider`)}
            render={(record: NotificationLogRecord) => record.provider || "—"}
          />
          <FunctionField
            source="messageType"
            label={translate(`${i18nBase}.fields.messageType`)}
            render={(record: NotificationLogRecord) => (
              <ChannelDeliveryBadge
                messageType={record.messageType}
                status={record.status}
                error={record.error}
              />
            )}
          />
        </CustomDatagrid>
      </List>
    </Box>
  );
};

export default NotificationLogsList;
